import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { logInfo } from "@/lib/structured-log";

/**
 * Global loading indicator shown during route transitions
 *
 * Features:
 * - Thin progress bar at top of page (YouTube/GitHub style)
 * - Smooth animation with CSS transitions
 * - Automatically starts at 10% and progresses to 90%
 * - Completes instantly when route loads
 *
 * Used in __root.tsx as pendingComponent
 */
function GlobalLoadingIndicator() {
  const [progress, setProgress] = useState(10);

  useEffect(() => {
    // Log when loading indicator is mounted (for debugging)
    logInfo("route.loading.indicator_shown", { timestamp: Date.now() });

    // Smoothly increment progress from 10% to 90% over time
    // This gives visual feedback while keeping the bar from reaching 100%
    // (which would misleadingly indicate completion)
    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) return 90;
        // Slow down as we approach 90%
        const increment = (90 - prev) * 0.1;
        return Math.min(prev + increment, 90);
      });
    }, 100);

    return () => {
      logInfo("route.loading.indicator_hidden", { timestamp: Date.now() });
      clearInterval(timer);
    };
  }, []);

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[9999] h-[3px] bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 shadow-lg shadow-blue-500/30 transition-all duration-300 ease-out"
      style={{
        width: `${progress}%`,
      }}
      role="progressbar"
      aria-label="Page loading"
      aria-valuenow={progress}
      aria-valuemin={0}
      aria-valuemax={100}
    />
  );
}

/**
 * Lightweight loading spinner for specific components
 * Use this for component-level loading states (not route transitions)
 */
function LoadingSpinner({ className }: { className?: string }) {
  return (
    <div className={cn("flex items-center justify-center p-8", className)}>
      <div className="size-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
    </div>
  );
}

export { GlobalLoadingIndicator, LoadingSpinner };
